<template>
  <div class="pp-wrapper" :class="[isDark ? 'pp-dark' : 'pp-light', customClass]">
    <!-- ── Header ─────────────────────────────────── -->
    <div class="pp-header">
      <div class="pp-header-left">
        <slot name="title">
          <div v-if="title || subtitle" class="pp-title-group">
            <span class="pp-title" v-if="title">{{ title }}</span>
            <span class="pp-subtitle" v-if="subtitle">{{ subtitle }}</span>
          </div>
        </slot>
      </div>

      <div class="pp-header-right">
        <!-- Extra controls/actions slot -->
        <slot name="header-actions" />

        <!-- Standardized Back Button in top right -->
        <v-tooltip top :open-delay="200" content-class="action-tooltip" v-if="showBack">
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              text
              @click="onBack"
              class="pp-back-btn"
              :style="{ color: buttonColor, borderColor: buttonColor }"
              v-on="on"
              v-bind="attrs"
            >
              <v-icon size="16" class="mr-1" :style="{ color: buttonColor }">mdi-arrow-left</v-icon>
              BACK
            </v-btn>
          </template>
          <span>{{ backLabel || 'Return to previous page' }}</span>
        </v-tooltip>
      </div>
    </div>

    <!-- ── Content ─────────────────────────────────── -->
    <div class="pp-content" :class="contentClass">
      <slot />
    </div>

    <!-- ── Footer ──────────────────────────────────── -->
    <div v-if="$slots.footer || showFooterButtons" class="pp-footer">
      <slot name="footer">
        <template v-if="showFooterButtons">
          <v-btn
            text
            class="btn-premium-cancel"
            @click="$emit('cancel')"
            :disabled="loading"
          >
            {{ cancelLabel || 'Cancel' }}
          </v-btn>
          <v-btn
            depressed
            class="btn-premium-primary"
            @click="$emit('submit')"
            :loading="loading"
          >
            {{ submitLabel || 'Submit' }}
          </v-btn>
        </template>
      </slot>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';

export default {
  name: 'PremiumPage',

  props: {
    title:        { type: String, default: '' },
    subtitle:     { type: String, default: '' },
    customClass:  { type: String, default: '' },
    contentClass: { type: String, default: '' },
    showBack:          { type: Boolean, default: false },
    backLabel:         { type: String, default: '' },
    showFooterButtons: { type: Boolean, default: false },
    submitLabel: { type: String, default: 'Submit' },
    submitIcon: { type: String, default: 'mdi-check' },
    cancelLabel: { type: String, default: 'Cancel' },
    loading:           { type: Boolean, default: false },
  },

  computed: {
    ...mapState('theme', ['isDark', 'primaryColor', 'buttonColor']),
    brandColor() {
      return this.primaryColor || '#0D9972';
    },
    btnColor() {
      return this.buttonColor || '#798BCB';
    }
  },

  methods: {
    onBack() {
      if (this.$listeners.back) {
        this.$emit('back');
      } else {
        this.$router.go(-1);
      }
    }
  }
};
</script>

<style scoped>
/* ── Wrapper ───────────────────────────── */
.pp-wrapper {
  border-radius: 16px;
  overflow: hidden;
  transition: background 0.2s, border-color 0.2s;
  width: 100%;
}
.pp-light {
  background: var(--surface, #ffffff);
  border: 1px solid var(--border, #E2E8F0);
  box-shadow: 0 1px 3px rgba(0,0,0,0.07), 0 2px 8px rgba(0,0,0,0.04);
}
.pp-dark {
  background: #1e293b;
  border: 1px solid rgba(255,255,255,0.1);
  box-shadow: 0 4px 16px rgba(0,0,0,0.35);
}

/* ── Header ───────────────────────────── */
.pp-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: nowrap;
  gap: 12px;
  padding: 16px;
  border-bottom: 1px solid var(--border, #E2E8F0);
  min-width: 0;
}
.pp-dark .pp-header {
  border-bottom-color: rgba(255,255,255,0.1);
  background: #1e293b;
}
.pp-header-left {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-grow: 1;
  min-width: 0;
  overflow: hidden;
}

.pp-header-right {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}

.pp-title-group { display: flex; flex-direction: column; }

.pp-title {
  font-family: 'Bricolage Grotesque', sans-serif;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--tx, #111827);
  line-height: 1.2;
}
.pp-dark .pp-title { color: #FAFAFA; }

.pp-subtitle {
  font-size: 12px;
  color: var(--tx3, #9CA3AF);
  margin-top: 2px;
}
.pp-dark .pp-subtitle {
  color: #A3A3A3;
}

.pp-back-btn {
  padding: 0 10px 0 10px !important;
  font-weight: 700 !important;
  font-size: 12px !important;
  letter-spacing: 0.05em !important;
  border-radius: 8px !important;
  border: 1.5px solid currentColor !important;
  background: transparent !important;
  height: 36px !important;
  opacity: 0.85;
  transition: opacity 0.2s, background 0.2s !important;
}
.pp-back-btn:hover {
  opacity: 1;
  background: rgba(255, 255, 255, 0.08) !important;
}

/* ── Content ──────────────────────────── */
.pp-content {
  padding: 0; 
}

.pp-footer {
  padding: 20px 24px;
  border-top: 1px solid var(--border, #E2E8F0);
  background: #F8FAFC;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 12px;
}
.pp-dark .pp-footer {
  background: #273548;
  border-top-color: rgba(255,255,255,0.1);
}

/* ── Responsive ───────────────────────────── */
@media (max-width: 600px) {
  .pp-header {
    flex-wrap: wrap;
    gap: 6px;
    padding: 10px 12px;
    align-items: flex-start;
  }
  .pp-header-left {
    flex: 1 1 100%;
    min-width: 0;
  }
  .pp-header-right {
    flex: 0 0 100%;
    justify-content: flex-end;
    gap: 6px;
  }
  .pp-title {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
  }
  .pp-subtitle {
    display: none;
  }
}
</style>

